import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import Glossy3DCard from "@/components/Glossy3DCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings as SettingsIcon, Copy, Check, KeyRound, ShieldCheck, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const SCOPES = [
  "admin.directory.user", "admin.directory.user.security", "admin.directory.user.alias",
  "admin.directory.group", "admin.directory.group.member", "admin.directory.orgunit",
  "admin.directory.device.mobile", "admin.directory.device.mobile.action",
  "admin.directory.domain", "admin.directory.rolemanagement", "drive", "cloud-identity.policies.readonly", "cloud-identity.allowlisteddomains.readonly", "cloud-identity.allowlisteddomains",
].map((s) => `https://www.googleapis.com/auth/${s}`);
const SCOPE_CSV = SCOPES.join(",");

function CopyBtn({ value, testid }) {
  const [done, setDone] = useState(false);
  return (
    <Button variant="outline" size="sm" className="rounded-xl bg-white/60" data-testid={testid}
      onClick={() => { try { navigator.clipboard.writeText(value); setDone(true); toast.success("Copied"); setTimeout(() => setDone(false), 1500); } catch { toast.error("Copy failed — select and copy manually"); } }}>
      {done ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </Button>
  );
}

export default function Settings() {
  const [form, setForm] = useState({ service_account_client_id: "", service_account_email: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm({
      service_account_client_id: r.data.service_account_client_id || "",
      service_account_email: r.data.service_account_email || "",
      notes: r.data.notes || "",
    })).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", form); toast.success("Settings saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="animate-fade-up max-w-3xl">
      <PageHeader title="Settings" subtitle="Save your shared delegation details for quick reuse across domains" icon={SettingsIcon} />

      <Glossy3DCard tilt={false} className="mb-4 p-6">
        <span className="spec-shine" />
        <h3 className="font-heading mb-1 flex items-center gap-2 text-lg font-semibold text-slate-800">
          <KeyRound className="h-5 w-5 text-emerald-600" />Domain-Wide Delegation
        </h3>
        <p className="mb-4 text-sm text-slate-500">This is the service-account Client ID you authorize in each domain's Admin Console. Stored here so it's never lost.</p>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Service-account Client ID</Label>
            <div className="flex gap-2">
              <Input value={form.service_account_client_id} onChange={(e) => setForm({ ...form, service_account_client_id: e.target.value })}
                placeholder="e.g. 115855635586634379534" className="font-mono" data-testid="settings-client-id-input" />
              <CopyBtn value={form.service_account_client_id} testid="copy-client-id" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Service-account email (optional)</Label>
            <Input value={form.service_account_email} onChange={(e) => setForm({ ...form, service_account_email: e.target.value })}
              placeholder="workspace-exporter@project.iam.gserviceaccount.com" className="font-mono" data-testid="settings-sa-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Notes (optional)</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Which Google project holds this SA, key location, etc." data-testid="settings-notes-input" />
          </div>
          <Button onClick={save} disabled={saving} className="glossy-emerald relative rounded-xl" data-testid="save-settings-button">
            <span className="spec-shine" />{saving ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </Glossy3DCard>

      <Glossy3DCard tilt={false} className="p-6">
        <span className="spec-shine" />
        <h3 className="font-heading mb-1 flex items-center gap-2 text-lg font-semibold text-slate-800">
          <ShieldCheck className="h-5 w-5 text-emerald-600" />OAuth Scopes to authorize
        </h3>
        <p className="mb-3 text-sm text-slate-500">Paste this exact list in each domain's Domain-wide delegation entry.</p>
        <div className="mb-3 flex justify-end"><CopyBtn value={SCOPE_CSV} testid="copy-scopes" /></div>
        <div className="max-h-52 overflow-y-auto rounded-2xl bg-slate-900 p-3 font-mono text-[11px] leading-relaxed text-emerald-300" data-testid="scopes-block">
          {SCOPES.map((s) => <div key={s}>{s}</div>)}
        </div>
        <a href="https://admin.google.com/ac/owl/domainwidedelegation" target="_blank" rel="noreferrer"
          className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:underline" data-testid="open-dwd-link">
          Open Domain-wide delegation in Admin Console <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </Glossy3DCard>
    </div>
  );
}
